<script setup>
import { i18n } from 'boot/i18n';
import {
  notifyWarning,
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrList,
  HrListAddBtn,
  HrListExpansion,
  HrListSectionHeader,
  HrListSelect,
  HrOverflowMenuBtn,
  HrTextField,
  HrWarningBanner,
} from 'src/design-system';
import { computed, ref } from 'vue';

import { CAP_PERIOD, STATED_UNIT } from '../../domain/constants/authorization';
import { AUTHORIZATION_ERRORS, servicesOnSeveralLimits } from '../../domain/model/Authorization';
import { presentLimit } from '../presenters/authorizationRowPresenter';
import { authorizationWindowLength } from '../presenters/authorizationWindowLength';
import AddApprovedServiceDialog from './AddApprovedServiceDialog.vue';
import ApprovedLimitEditor from './ApprovedLimitEditor.vue';
import CalendarDateField from './CalendarDateField.vue';

/**
 * Records one authorization letter.
 *
 * `HrFormDialog`, NOT `HrVersionedFormDialog`, on that dialog's own advice: an authorization has no
 * effective date and no history yet, so its "when does this take effect" step would ask a question
 * with one answer. When the amendment flow lands, this becomes it.
 *
 * `CalendarDateField` FOR THE WINDOW: there is still no `HrDateField`, and `HrDate` is a bare
 * calendar contracted on 'YYYY/MM/DD' where the window is stored as 'YYYY-MM-DD'.
 *
 * THE DRAFT IS FLAT AND RAW, because validation judges what the biller typed. Once the factory has
 * run, a date it could not read and a deliberately open one are both null and no longer tellable
 * apart — and an unreadable end date would turn a letter that expires into one that never does.
 */
defineOptions({ name: 'EditAuthorizationDialog' });

const props = defineProps({
  /**
   * Funding sources selectable for this client, as `{ label, value }`: the payers on its coverage.
   * Empty means it has none, and the picker says to add the coverage first.
   */
  fundingSourceOptions: {
    type: Array,
    default: () => [],
  },
  /** Treatment services by funding source id, for the limit picker. */
  serviceOptionsByFundingSource: {
    type: Object,
    default: () => ({}),
  },
  /** Distinct place-of-service codes the clinic uses, as `{ label, value }`. */
  settingOptions: {
    type: Array,
    default: () => [],
  },
  /** The clinic's settings could not be read; the editor says so rather than showing nothing. */
  settingsUnavailable: {
    type: Boolean,
    default: false,
  },
  /** Validates a draft and returns located error objects; supplied by the session. */
  validate: {
    type: Function,
    required: true,
  },
  /** Names a treatment service for the duplicate-service warning. */
  treatmentServiceName: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['ok']);

const dialog = ref(null);
const servicePicker = ref(null);

/*
 * THE FIRST CAP ONLY. "N units for the auth period" is far and away the commonest letter, so the
 * biller types the figure and moves on. A SECOND cap must stay blank: it exists to pace the total
 * over a shorter calendar, and two caps sharing a period is `LIMIT_DUPLICATE_CAP_PERIOD`, so
 * copying these defaults into `addCap` would open the row already invalid.
 *
 * The figure stays null. Pre-filling the unit and the calendar offers the usual shape; pre-filling
 * the amount would put a number on the record that no letter states.
 */
const blankLimit = (services = []) => ({
  limitId: null,
  services,
  placeOfServiceCodes: [],
  caps: [{
    figure: null,
    statedUnit: STATED_UNIT.UNITS,
    period: CAP_PERIOD.TOTAL,
    resetBehavior: null,
  }],
});

// No entries to begin with: the add button is the empty state, and every entry is born naming a
// service, so no row ever renders an unlabeled header.
const blankDraft = () => ({
  fundingSourceId: null,
  authorizationNumber: null,
  periodStart: null,
  periodEnd: null,
  limits: [],
});

const draft = ref(blankDraft());

/** Which row is expanded. Exactly one, ever: with four services the dialog scrolls otherwise. */
const openLimit = ref(0);

const isOpen = (index) => openLimit.value === index;

const setOpen = (index, opened) => {
  openLimit.value = opened ? index : null;
};

const errors = computed(() => props.validate(draft.value));

/** Is this header-level code among the current errors? Located codes name a limit; these do not. */
const has = (code) => errors.value.some((error) => error.limitIndex === null && error.code === code);

const message = (code) => i18n.t(`billingAuthorizations.errors.${code}`);

const fundingSourceRules = [
  () => !has(AUTHORIZATION_ERRORS.FUNDING_SOURCE_REQUIRED)
    || message(AUTHORIZATION_ERRORS.FUNDING_SOURCE_REQUIRED),
];

// UNREADABLE_PERIOD_DATE means a date field handed the aggregate something that is not a calendar
// date. It cannot happen through the current picker; it guards against a date input that speaks
// another type being swapped in. Surfaced on both fields, since either can carry it.
const unreadableDateRule = () => !has(AUTHORIZATION_ERRORS.UNREADABLE_PERIOD_DATE)
  || message(AUTHORIZATION_ERRORS.UNREADABLE_PERIOD_DATE);

const periodStartRules = [
  () => !has(AUTHORIZATION_ERRORS.PERIOD_START_REQUIRED)
    || message(AUTHORIZATION_ERRORS.PERIOD_START_REQUIRED),
  unreadableDateRule,
];

const periodEndRules = [
  () => !has(AUTHORIZATION_ERRORS.PERIOD_END_REQUIRED)
    || message(AUTHORIZATION_ERRORS.PERIOD_END_REQUIRED),
  () => !has(AUTHORIZATION_ERRORS.PERIOD_END_BEFORE_START)
    || message(AUTHORIZATION_ERRORS.PERIOD_END_BEFORE_START),
  unreadableDateRule,
];

// BY POSITION, NOT BY ID. A limit the biller has just added holds a null id — the unit of work
// mints one at validate time and the draft never sees it — so an id-matched row would show nothing
// while Save stayed disabled.
const errorsForLimit = (index) => {
  return errors.value.filter((error) => error.limitIndex === index);
};

/**
 * A WARNING, never a block: a funder scoping one service two ways by setting states it twice, so
 * refusing would make a real approval unrecordable. Usually it is the same row entered twice.
 */
const repeatedServices = computed(() => {
  return servicesOnSeveralLimits(draft.value).map(props.treatmentServiceName);
});

const serviceOptions = computed(() => {
  return props.serviceOptionsByFundingSource[draft.value.fundingSourceId] ?? [];
});

/**
 * The services on offer are funder-scoped, so with no funder the picker would be empty — and an
 * empty picker looks identical to a funder with no services configured, which is a different problem
 * with a different fix. Disabled rather than hidden, so the shape of the form stays visible.
 */
const hasFunder = computed(() => draft.value.fundingSourceId !== null);

/**
 * Which entry the service picker is editing, or null when adding. Held here rather than passed back
 * through the event: the picker answers "which services", and what that answer is FOR belongs here.
 */
const editingLimitIndex = ref(null);

const usedServiceIds = computed(() => {
  return draft.value.limits
    .filter((_limit, index) => index !== editingLimitIndex.value)
    .flatMap((limit) => limit.services.map((s) => s.treatmentServiceId));
});

const treatmentServiceNameById = computed(() => {
  return new Map(serviceOptions.value.map(({ value, label }) => [value, label]));
});

/** How long the window runs — a sanity check against the paper, in the unit that length calls for. */
const windowLength = computed(() => {
  return authorizationWindowLength(draft.value.periodStart, draft.value.periodEnd);
});

const limitLabel = (index) => {
  return presentLimit(draft.value.limits[index], treatmentServiceNameById.value).services;
};

/**
 * The collapsed row's live echo, built by the presenter the saved rows use.
 *
 * The presenter skips a cap that is not fully stated, so a partly-filled row still echoes the cap
 * that IS stated. An untouched row echoes nothing rather than saying so: "No figure stated yet" on a
 * form just opened reads as a failure the biller has already committed.
 *
 * A LIMIT-LEVEL FAILURE DISPLACES THE ECHO, because exactly one row is open at a time: a collapsed
 * row that cannot be saved would otherwise hold Save disabled with its reason hidden inside it. Cap
 * failures are deliberately not promoted here — every row is born without a figure, so they would
 * fire the moment one was added.
 */
const limitCaption = (index) => {
  const blocking = errorsForLimit(index).find((error) => error.capIndex === null);

  return blocking
    ? message(blocking.code)
    : presentLimit(draft.value.limits[index], treatmentServiceNameById.value).summary || null;
};

const editingId = ref(null);

/**
 * Opens blank, or on an existing authorization flattened back to the shape the form holds: a
 * `period` value object handed to a date field would arrive as an object, not a calendar string.
 */
function show(authorization = null) {
  editingId.value = authorization?.authorizationId ?? null;
  draft.value = authorization === null ? blankDraft() : {
    fundingSourceId: authorization.fundingSourceId,
    authorizationNumber: authorization.authorizationNumber,
    periodStart: authorization.period.start,
    periodEnd: authorization.period.end,
    limits: authorization.limits.map((limit) => ({
      limitId: limit.limitId,
      services: limit.services.map((service) => ({ ...service })),
      placeOfServiceCodes: [...limit.placeOfServiceCodes],
      caps: limit.caps.map((cap) => ({ ...cap })),
    })),
  };
  openLimit.value = 0;
  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

/**
 * Changing the funder clears the limits, which would otherwise point at services this payer does
 * not cover.
 *
 * ON THE CONTROL, NOT ON A WATCHER. `show()` assigns the funder and the limits in one go, so a
 * watcher could not tell a biller picking a different payer from the form loading a record — and it
 * cleared the limits it had just loaded, silently emptying every authorization opened after the
 * first.
 */
const onFunderChange = (fundingSourceId) => {
  if (fundingSourceId !== draft.value.fundingSourceId) {
    draft.value.limits = [];
  }
  draft.value.fundingSourceId = fundingSourceId;
};

/**
 * Adds ONE entry covering every service picked. THE SELECTION IS ONE APPROVED AMOUNT'S SERVICE SET,
 * not a list of amounts to create: splitting it into an entry each would multiply the approved
 * capacity by the number of services, silently.
 */
const addLimit = () => {
  // An empty picker is a dialog whose Confirm does nothing, with nothing on it to say why. The
  // funder having no services configured is the actual answer, so say that instead of opening it.
  if (serviceOptions.value.length === 0) {
    notifyWarning(i18n.t('billingAuthorizations.hintNoCoveredServices'));
    return;
  }

  editingLimitIndex.value = null;
  servicePicker.value?.show();
};

const patchLimit = (index, limit) => {
  draft.value.limits = draft.value.limits.map((entry, position) => {
    return position === index ? limit : entry;
  });
};

const editLimitServices = (index) => {
  editingLimitIndex.value = index;
  servicePicker.value?.show(
    draft.value.limits[index].services.map(({ treatmentServiceId }) => treatmentServiceId),
  );
};

const onServicesPicked = (treatmentServiceIds) => {
  const services = treatmentServiceIds.map((treatmentServiceId) => ({ treatmentServiceId }));

  if (editingLimitIndex.value !== null) {
    // Only the service set changes; the caps and the settings the biller already entered stay.
    patchLimit(editingLimitIndex.value, {
      ...draft.value.limits[editingLimitIndex.value],
      services,
    });
    return;
  }

  openLimit.value = draft.value.limits.length;
  draft.value.limits = [...draft.value.limits, blankLimit(services)];
};

const removeLimit = (index) => {
  draft.value.limits = draft.value.limits.filter((_limit, position) => position !== index);
  openLimit.value = Math.min(index, draft.value.limits.length - 1);
};

/** Removal lives here rather than inside the body, so an entry can be dropped without opening it. */
const entryMenuOptions = (index) => ([
  {
    label: i18n.t('billingAuthorizations.actionEditServices'),
    icon: 'edit',
    action: () => editLimitServices(index),
  },
  {
    group: 'destructive',
    options: [{
      label: i18n.t('billingAuthorizations.actionRemoveLimit'),
      icon: 'delete',
      destructive: true,
      action: () => removeLimit(index),
    }],
  }]);

function onSubmit() {
  if (errors.value.length > 0) {
    return;
  }

  emit('ok', { authorizationId: editingId.value, draft: { ...draft.value } });
  hide();
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="md"
    :title="editingId === null
      ? i18n.t('billingAuthorizations.titleRecord')
      : i18n.t('billingAuthorizations.titleEdit')"
    data-e2e="dialog-edit-authorization"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-list-select
        :model-value="draft.fundingSourceId"
        class="col-12 col-sm-6"
        :options="fundingSourceOptions"
        :label="i18n.t('billingAuthorizations.fields.fundingSource')"
        :rules="fundingSourceRules"
        :hint="fundingSourceOptions.length === 0 ? i18n.t('billingAuthorizations.hintNoCoverage') : null"
        hide-bottom-space
        data-e2e="select-authorization-funding-source"
        @update:model-value="onFunderChange"
      />

      <hr-text-field
        v-model="draft.authorizationNumber"
        class="col-12 col-sm-6"
        :label="i18n.t('billingAuthorizations.fields.authorizationNumber')"
        :hint="i18n.t('billingAuthorizations.hintNumberPending')"
        data-e2e="input-authorization-number"
      />

      <calendar-date-field
        v-model="draft.periodStart"
        class="col-12 col-sm-6"
        :label="i18n.t('billingAuthorizations.fields.periodStart')"
        :rules="periodStartRules"
        data-e2e="input-authorization-period-start"
      />

      <calendar-date-field
        v-model="draft.periodEnd"
        class="col-12 col-sm-6"
        :label="i18n.t('billingAuthorizations.fields.periodEnd')"
        :hint="windowLength"
        :rules="periodEndRules"
        data-e2e="input-authorization-period-end"
      />

      <div
        v-if="repeatedServices.length > 0"
        class="col-12"
      >
        <hr-warning-banner
          :message="i18n.t('billingAuthorizations.warnings.serviceOnSeveralLimits', {
            services: repeatedServices.join(', '),
          })"
        />
      </div>

      <hr-list class="col-12">
        <hr-list-section-header>
          {{ i18n.t('billingAuthorizations.sectionApproved') }}
        </hr-list-section-header>

        <hr-list-expansion
          v-for="(limit, index) in draft.limits"
          :key="index"
          :model-value="isOpen(index)"
          segmented
          flush
          :label="limitLabel(index)"
          :caption="limitCaption(index)"
          data-e2e="expansion-authorization-limit"
          @update:model-value="setOpen(index, $event)"
        >
          <template #trailing>
            <hr-overflow-menu-btn
              :options="entryMenuOptions(index)"
              data-e2e="btn-authorization-entry-menu"
            />
          </template>

          <!-- q-expansion-item pads nothing, so the revealed content would sit flush against the
               segmented card's fill. -->
          <div class="q-pa-md">
            <approved-limit-editor
              :model-value="limit"
              :setting-options="settingOptions"
              :settings-unavailable="settingsUnavailable"
              :errors="errorsForLimit(index)"
              @update:model-value="patchLimit(index, $event)"
            />
          </div>
        </hr-list-expansion>

        <!-- With no entries yet this button IS the empty state, as on the coverage panel. `flush`
             to match the entries above, which are flush because this list sits in a form grid. -->
        <hr-list-add-btn
          :variant="draft.limits.length ? 'flat' : 'outlined'"
          :label="i18n.t('billingAuthorizations.actionAddLimit')"
          :disable="!hasFunder"
          flush
          data-e2e="btn-add-authorization-limit"
          @click="addLimit"
        />
      </hr-list>
    </div>

    <add-approved-service-dialog
      ref="servicePicker"
      :service-options="serviceOptions"
      :used-service-ids="usedServiceIds"
      @ok="onServicesPicked"
    />

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonSave')"
        type="submit"
        :disable="!hasFunder || errors.length > 0"
        data-e2e="btn-save-authorization"
      />
    </template>
  </hr-form-dialog>
</template>
